<template>
  <v-card>
    <v-form @submit.prevent="submit">
      <v-card-title>
        {{ t('cs_title', { park_bnum: cs['park_bnum'], bnum: cs['bnum'] }) }}
      </v-card-title>
      <v-card-text>
        <v-row no-gutters>
          <v-col class="pe-3">
            <v-text-field
              v-model="form.qrcodeid.value.value"
              :error-messages="form.qrcodeid.errorMessage.value"
              :label="t('qr_code')"
              density="compact"
            />
          </v-col>
          <v-col class="ps-3">
            <v-text-field
              v-model="form.price.value.value"
              :error-messages="form.price.errorMessage.value"
              :label="t('price_cents')"
              density="compact"
            />
          </v-col>
        </v-row>
        <v-row no-gutters class="pt-2">
          <v-col class="pe-3">
            <v-text-field
              :label="t('money_dest')"
              density="compact"
              readonly
              :model-value="mdVisualName(cs['money_receiver'])"
            />
          </v-col>
          <v-col class="ps-3" cols="auto">
            <v-text-field
              :label="t('logo_id')"
              density="compact"
              readonly
              :model-value="cs['override_logo']"
            />
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col>
            <v-card elevation="4">
              <v-card-title>
                <v-row>
                  <v-col cols="auto">
                    <svg class="d-block" width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="16" cy="16" r="14" :fill="colorCharge" stroke="#000" stroke-linejoin="bevel"
                              stroke-width="4"/>
                    </svg>
                  </v-col>
                  <v-col v-if="isCharging">
                    {{ t('charging') }}
                  </v-col>
                  <v-col v-else>
                    {{ t('not_charging') }}
                  </v-col>
                </v-row>
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="8">
                    <template v-if="cs['last_charge'] !== null">
                      <p>{{ t('start_time', { time: new Date(cs['last_charge']['start']).toLocaleString() }) }}</p>
                      <p v-if="!isCharging">
                        {{ t('stop_time', { time: new Date(cs['last_charge']['stop']).toLocaleString() }) }}</p>
                      <p>{{ t('energy_wh', { energy: cs['last_charge']['energy_wh'] }) }}</p>
                      <p>{{ t('user_label', { username: chargeUsername || t('error') }) }}</p>
                    </template>
                  </v-col>
                  <v-col cols="4" class="d-flex align-center justify-center">
                    <template v-if="isCharging">
                      <v-btn v-if="!doingOnCharge" variant="tonal" @click="closeCharge" color="red">
                        {{ t('close') }}
                      </v-btn>
                      <v-progress-circular v-else indeterminate color="red"/>
                    </template>
                    <template v-else-if="cs['qrcodeid']!== null">
                      <v-btn v-if="!doingOnCharge" variant="tonal" @click="openCharge" color="green">
                        {{ t('start') }}
                      </v-btn>
                      <v-progress-circular v-else indeterminate color="green"/>
                    </template>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <div class="flex flex-col justify-self-center align-center" style="width: 100%">
          <v-btn width="100%" class="border-md" type="button" :loading="sendingUnlock" @click="unlockCnnectorSubmit">{{
              t('unlock_connector')
            }}
          </v-btn>
          <v-btn width="100%" class="border-md mt-2" type="submit">{{ t('save') }}</v-btn>
        </div>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script setup>
import { ref, toRefs, computed, onMounted, watch } from 'vue';
import { fetchUser } from "../../remote/user.js";
import { forceCloseCharge, startCharge, unlockConnector } from "../../remote/park.js";
import * as yup from 'yup';
import { useModifyCSBase } from "./base.js";
import { useI18n } from "vue-i18n";

const { t } = useI18n({ inheritLocale: true, useScope: 'local' });

// General State
const useAlerts = defineModel('useAlerts');
const loadingOverlay = defineModel('loadingOverlay');
const props = defineProps(['cs', 'moneyDests']);
const { cs, moneyDests } = toRefs(props);
const emit = defineEmits(['updated']);

const chargeUsername = ref(null);
const doingOnCharge = ref(false);

const sendingUnlock = ref(false);

const isCharging = computed(() => {
  if (cs.value['last_charge'] === undefined || cs.value['last_charge'] === null) return false;
  return cs.value['last_charge']['stop'] === null;  // null = is charging!
});

const colorCharge = computed(() => {
  if (isCharging.value) return '#00FF00';
  return '#FF0000';
});

function mdVisualName(mdId) {
  return moneyDests.value.find(el => el['id'] === mdId)?.visual_name || null;
}

async function unlockCnnectorSubmit() {
  sendingUnlock.value = true;
  try {
    await unlockConnector(useAlerts, cs.value['bnum']);
    useAlerts.value.successAlert("Unlock Request sent", "It should unlock in some seconds");
  } finally {
    sendingUnlock.value = false;
  }
}

async function updateChargeUser() {
  if (cs.value['last_charge'] !== undefined && cs.value['last_charge'] !== null) {
    try {
      console.log(cs.value['last_charge']['user']);
      let user = (await fetchUser(cs.value['last_charge']['user']))['username'];
      if (user.includes('$')) {
        user = t('pms_user', { user_id: cs.value['last_charge']['user'] });
      }
      chargeUsername.value = user;
    } catch (e) {
      console.error(e);
      chargeUsername.value = t('error');
    }
  }
}

watch(cs, updateChargeUser);

onMounted(updateChargeUser);

async function closeCharge() {
  doingOnCharge.value = true;
  await forceCloseCharge(useAlerts, cs.value['park'], cs.value['park_bnum']);
  doingOnCharge.value = false;
  emit('updated');
}

async function openCharge() {
  doingOnCharge.value = true;
  await startCharge(useAlerts, cs.value['qrcodeid']);
  doingOnCharge.value = false;
  emit('updated');
}

// FORM
// noinspection JSCheckFunctionSignatures
const schema = yup.object({
  qrcodeid: yup.number().positive().nullable().transform((value, originalValue) => originalValue === '' ? null : value),
  price: yup.number(),
});

const editPayload = (values) => {
  values.qrcodeid = values.qrcodeid === "" ? null : values.qrcodeid;
};

const { form, submit } = useModifyCSBase(
  useAlerts,
  loadingOverlay,
  cs,
  schema,
  emit,
  undefined,
  undefined,
  editPayload
);
</script>

<i18n>
{
  "en": {
    "cs_title": "CS {park_bnum} - BNUM: {bnum}",
    "qr_code": "QR Code",
    "price_cents": "Price in € cents",
    "money_dest": "Money Dest",
    "logo_id": "Logo ID",
    "charging": "Charging",
    "not_charging": "Not Charging",
    "start_time": "Start: {time}",
    "stop_time": "Stop: {time}",
    "energy_wh": "Energy: {energy} Wh",
    "user_label": "User {username}",
    "close": "Close",
    "start": "Start",
    "save": "Save",
    "unlock_connector": "Unlock Motor (Modbus only)",
    "pms_user": "PMS User {user_id}",
    "error": "error"
  },
  "it": {
    "cs_title": "CS {park_bnum} - BNUM: {bnum}",
    "qr_code": "Codice QR",
    "price_cents": "Prezzo in centesimi di €",
    "money_dest": "Destinatario Denaro",
    "logo_id": "ID Logo",
    "charging": "In Carica",
    "not_charging": "Non in Carica",
    "start_time": "Inizio: {time}",
    "stop_time": "Fine: {time}",
    "energy_wh": "Energia: {energy} Wh",
    "user_label": "Utente {username}",
    "close": "Chiudi",
    "start": "Avvia",
    "save": "Salva",
    "unlock_connector": "Sblocca Motore (solo Modbus)",
    "pms_user": "Utente PMS {user_id}",
    "error": "errore"
  },
  "fr": {
    "cs_title": "CS {park_bnum} - BNUM: {bnum}",
    "qr_code": "Code QR",
    "price_cents": "Prix en centimes d'€",
    "money_dest": "Destination Argent",
    "logo_id": "ID Logo",
    "charging": "En Charge",
    "not_charging": "Pas en Charge",
    "start_time": "Début: {time}",
    "stop_time": "Fin: {time}",
    "energy_wh": "Énergie: {energy} Wh",
    "user_label": "Utilisateur {username}",
    "close": "Fermer",
    "start": "Démarrer",
    "save": "Enregistrer",
    "unlock_connector": "Déverrouiller le Moteur (Modbus uniquement)",
    "pms_user": "Utilisateur PMS {user_id}",
    "error": "erreur"
  }
}
</i18n>

<style scoped>

</style>
